import { makeId, clone } from './geometry.js';
import {
  defaultLayers,
  getDefaultPanelModel,
  normalizeSolarPanelModel,
  setSolarPanelModelsFromDatabase,
} from './drawingTools.js';
import { DEFAULT_PIXELS_PER_INCH } from './measurements.js';
import { createMapConfigForLocation, latLngToMapPixel } from './googleMaps.js';
import {
  clearSupabaseAuthStorage,
  isInvalidRefreshTokenError,
  isSupabaseConfigured,
  supabase,
} from './supabaseClient.js';

const STORAGE_KEY = 'n3bula-local-data-v2';
const SOLAR_PANELS_STORAGE_KEY = 'n3bula-local-solar-panels-v1';
const SESSION_KEY = 'n3bula-session-v1';
export const APP_SESSION_DURATION_MS = 12 * 60 * 60 * 1000;
export const APP_SESSION_DURATION_HOURS = 12;

function nowIso() {
  return new Date().toISOString();
}

function normalizePropertyDraft(propertyLine = null) {
  if (!propertyLine) return { points: [], source: 'satellite' };
  if (Array.isArray(propertyLine)) return { points: propertyLine, source: 'satellite', previewWidth: 1280, previewHeight: 1280 };
  return {
    points: propertyLine.points || [],
    source: propertyLine.source || 'satellite',
    zoom: propertyLine.zoom || 20,
    previewWidth: Number(propertyLine.previewWidth) || 0,
    previewHeight: Number(propertyLine.previewHeight) || 0,
    addressLocation: propertyLine.addressLocation || null,
    closed: propertyLine.closed !== false,
    geoPoints: Array.isArray(propertyLine.geoPoints) ? propertyLine.geoPoints : [],
    previewPixelsPerInch: Number(propertyLine.previewPixelsPerInch) || DEFAULT_PIXELS_PER_INCH,
  };
}

function scalePropertyLineToMap(propertyDraft, map = {}) {
  const draft = normalizePropertyDraft(propertyDraft);
  if (!draft.points?.length) return [];

  const captureWidth = Number(map.captureWidth) || Number(map.snapshotPreviewWidth) || Number(draft.previewWidth) || Number(map.width) || 640;
  const captureHeight = Number(map.captureHeight) || Number(map.snapshotPreviewHeight) || Number(draft.previewHeight) || Number(map.height) || 640;
  const renderScale = Number(map.mapDisplayScale) || 1;
  const zoom = Number(map.googleStaticZoom || draft.zoom || 20);
  const center = map.addressLocation || draft.addressLocation || null;

  if (center && Array.isArray(draft.geoPoints) && draft.geoPoints.length === draft.points.length) {
    const converted = draft.geoPoints
      .map((point) => latLngToMapPixel(point, center, zoom, captureWidth, captureHeight))
      .filter(Boolean)
      .map((point) => ({ x: point.x * renderScale, y: point.y * renderScale }));
    if (converted.length === draft.points.length) return converted;
  }

  if (map.snapshotUsesPreviewScale) {
    return draft.points.map((point) => ({ x: point.x * renderScale, y: point.y * renderScale }));
  }

  const mapWidth = Number(map.width) || captureWidth * renderScale;
  const mapHeight = Number(map.height) || captureHeight * renderScale;
  const previewWidth = Number(draft.previewWidth) || captureWidth;
  const previewHeight = Number(draft.previewHeight) || captureHeight;
  const scaleX = mapWidth / previewWidth;
  const scaleY = mapHeight / previewHeight;
  const previewCenter = { x: previewWidth / 2, y: previewHeight / 2 };
  const mapCenter = { x: mapWidth / 2, y: mapHeight / 2 };
  return draft.points.map((point) => ({
    x: mapCenter.x + (point.x - previewCenter.x) * scaleX,
    y: mapCenter.y + (point.y - previewCenter.y) * scaleY,
  }));
}

function blankElements() {
  return {
    roofs: [],
    propertyLines: [],
    obstructions: [],
    fireSetbacks: [],
    solarPanels: [],
    attachments: [],
    rafters: [],
  };
}

function mapSizeFromPropertyDraft(propertyDraft, fallbackWidth = 1280, fallbackHeight = 720) {
  const previewWidth = Number(propertyDraft?.previewWidth) || fallbackWidth;
  const previewHeight = Number(propertyDraft?.previewHeight) || fallbackHeight;
  if (!previewWidth || !previewHeight) return { width: fallbackWidth, height: fallbackHeight };
  return {
    width: Math.max(320, Math.round(previewWidth)),
    height: Math.max(320, Math.round(previewHeight)),
  };
}

function defaultMap(propertyLine = null, addressLocation = null) {
  const propertyDraft = normalizePropertyDraft(propertyLine);
  const { width, height } = mapSizeFromPropertyDraft(propertyDraft);
  const location = propertyDraft.addressLocation || addressLocation || null;
  const mapConfig = location
    ? createMapConfigForLocation(location, {
        zoom: propertyDraft.zoom || 20,
        width: propertyDraft.previewWidth || width,
        height: propertyDraft.previewHeight || height,
        previewWidth: propertyDraft.previewWidth || width,
        previewHeight: propertyDraft.previewHeight || height,
        scale: 2,
        previewPixelsPerInch: propertyDraft.previewPixelsPerInch || undefined,
      })
    : {};
  const scaledPixelsPerInch = location ? (mapConfig.pixelsPerInch || propertyDraft.previewPixelsPerInch || DEFAULT_PIXELS_PER_INCH) : DEFAULT_PIXELS_PER_INCH;
  return {
    source: 'Google Satellite',
    opacity: 0.9,
    x: 0,
    y: 0,
    rotation: 0,
    width,
    height,
    pixelsPerInch: DEFAULT_PIXELS_PER_INCH,
    imageDataUrl: '',
    propertyLineDraft: propertyDraft,
    addressLocation: location,
    ...mapConfig,
    source: 'Google Satellite',
    pixelsPerInch: scaledPixelsPerInch,
  };
}

function createSeedData() {
  const projectId = makeId('project');
  const designId = makeId('design');
  const created = nowIso();
  const addressLocation = { lat: 38.2736, lng: -122.0345 };
  return {
    projects: [
      {
        id: projectId,
        projectName: '2474 Orchid St Solar Design',
        address: '2474 Orchid St, Fairfield, CA 94533',
        createdBy: 'Jhunel Bernales',
        createdAt: created,
        lastModified: created,
        designIds: [designId],
        propertyLine: [
          { x: 120, y: 90 },
          { x: 520, y: 110 },
          { x: 500, y: 420 },
          { x: 105, y: 400 },
        ],
        addressLocation,
      },
    ],
    designs: [
      {
        id: designId,
        projectId,
        designName: 'Design A',
        solarPanelModel: defaultSolarPanelModelName(),
        solarPanelQuantity: 0,
        createdAt: created,
        createdBy: 'Jhunel Bernales',
        lastModified: created,
        layers: defaultLayers(),
        map: defaultMap(null, addressLocation),
        elements: blankElements(),
      },
    ],
  };
}

function localLoadData() {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    const seed = createSeedData();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(seed));
    return seed;
  }
  try {
    const parsed = JSON.parse(raw);
    return {
      projects: parsed.projects || [],
      designs: parsed.designs || [],
    };
  } catch {
    const seed = createSeedData();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(seed));
    return seed;
  }
}

function localSaveData(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function projectFromRow(row, designIds = []) {
  return {
    id: row.id,
    projectName: row.project_name || '',
    address: row.address || '',
    createdBy: row.created_by || 'N3bula User',
    createdAt: row.created_at,
    lastModified: row.last_modified,
    designIds: designIds.length ? designIds : row.design_ids || [],
    propertyLine: row.property_line || [],
    propertyLineDraft: row.property_line_draft || { points: [], source: 'satellite' },
    addressLocation: row.address_location || null,
  };
}

function designFromRow(row) {
  return {
    id: row.id,
    projectId: row.project_id,
    designName: row.design_name || 'Design A',
    solarPanelModel: row.solar_panel_model || defaultSolarPanelModelName(),
    solarPanelQuantity: Number(row.solar_panel_quantity) || 0,
    createdAt: row.created_at,
    createdBy: row.created_by || 'N3bula User',
    lastModified: row.last_modified,
    layers: row.layers || defaultLayers(),
    map: row.map || defaultMap(),
    elements: { ...blankElements(), ...(row.elements || {}) },
  };
}

function projectToRow(project) {
  return {
    id: project.id,
    project_name: project.projectName,
    address: project.address,
    created_by: project.createdBy,
    created_at: project.createdAt,
    last_modified: project.lastModified,
    design_ids: project.designIds || [],
    property_line: project.propertyLine || [],
    property_line_draft: project.propertyLineDraft || { points: [], source: 'satellite' },
    address_location: project.addressLocation || null,
  };
}

function designToRow(design) {
  return {
    id: design.id,
    project_id: design.projectId,
    design_name: design.designName,
    solar_panel_model: design.solarPanelModel,
    solar_panel_quantity: Number(design.solarPanelQuantity) || 0,
    created_at: design.createdAt,
    created_by: design.createdBy,
    last_modified: design.lastModified,
    layers: design.layers || defaultLayers(),
    map: design.map || defaultMap(),
    elements: { ...blankElements(), ...(design.elements || {}) },
  };
}

async function requireSupabase() {
  if (!isSupabaseConfigured || !supabase) return null;
  return supabase;
}

function clearAuthState() {
  clearSession();
  clearSupabaseAuthStorage();
}

function isRowLevelSecurityError(error) {
  const message = String(error?.message || error || '').toLowerCase();
  const code = String(error?.code || '').toLowerCase();
  return code === '42501' || message.includes('row-level security') || message.includes('row level security');
}

function friendlySupabaseError(error, action = 'Supabase request') {
  if (!error) return new Error(`${action} failed.`);
  if (isInvalidRefreshTokenError(error)) {
    clearAuthState();
    return new Error('Your saved login session expired or became invalid. Please sign in again.');
  }
  if (isRowLevelSecurityError(error)) {
    return new Error(`${action} failed: Supabase RLS is blocking this request. Run supabase/sql/solar_panels_rls_policies.sql in your Supabase SQL Editor to allow signed-in users to manage the shared solar panel database.`);
  }
  return new Error(`${action} failed: ${error.message || String(error)}`);
}


function localSolarPanelSeedRows() {
  return [
    {
      id: makeId('panel_model'),
      manufacturer: 'SEG Solar',
      model: 'SEG-440-BTD-BG',
      watts: 440,
      width_in: 44.65,
      height_in: 67.8,
      thickness_in: 1.38,
      active: true,
      created_at: nowIso(),
      updated_at: nowIso(),
    },
  ];
}

function panelRowFromStorage(row) {
  const normalized = normalizeSolarPanelModel(row);
  if (!normalized) return null;
  return {
    ...normalized,
    manufacturer: normalized.manufacturer || '',
    active: row?.active !== false,
  };
}

function localLoadSolarPanelRows() {
  const raw = localStorage.getItem(SOLAR_PANELS_STORAGE_KEY);
  if (!raw) {
    const seed = localSolarPanelSeedRows();
    localStorage.setItem(SOLAR_PANELS_STORAGE_KEY, JSON.stringify(seed));
    return seed;
  }
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    const seed = localSolarPanelSeedRows();
    localStorage.setItem(SOLAR_PANELS_STORAGE_KEY, JSON.stringify(seed));
    return seed;
  }
}

function localSaveSolarPanelRows(rows = []) {
  localStorage.setItem(SOLAR_PANELS_STORAGE_KEY, JSON.stringify(rows));
}

function solarPanelPayload(panel = {}) {
  const manufacturer = String(panel.manufacturer || '').trim();
  const model = String(panel.model || panel.name || '').trim();
  const watts = Number(panel.watts);
  const widthIn = Number(panel.widthIn ?? panel.width_in);
  const heightIn = Number(panel.heightIn ?? panel.height_in);
  const thicknessRaw = panel.thicknessIn ?? panel.thickness_in;
  const thicknessIn = thicknessRaw === '' || thicknessRaw === null || thicknessRaw === undefined ? null : Number(thicknessRaw);

  if (!manufacturer) throw new Error('Manufacturer is required.');
  if (!model) throw new Error('Model is required.');
  if (!Number.isFinite(watts) || watts <= 0) throw new Error('Watts must be greater than 0.');
  if (!Number.isFinite(widthIn) || widthIn <= 0) throw new Error('Width must be greater than 0.');
  if (!Number.isFinite(heightIn) || heightIn <= 0) throw new Error('Height must be greater than 0.');
  if (thicknessIn !== null && (!Number.isFinite(thicknessIn) || thicknessIn < 0)) throw new Error('Thickness must be blank or 0 or greater.');

  return {
    manufacturer,
    model,
    watts,
    width_in: widthIn,
    height_in: heightIn,
    thickness_in: thicknessIn,
    active: panel.active !== false,
  };
}

function solarPanelFromRow(row) {
  const normalized = normalizeSolarPanelModel(row);
  if (!normalized) return null;
  return {
    ...normalized,
    manufacturer: normalized.manufacturer || '',
    active: row?.active !== false,
  };
}

function defaultSolarPanelModelName() {
  return getDefaultPanelModel()?.name || 'SEG-440-BTD-BG';
}

let solarPanelModelsCache = null;

export async function loadSolarPanelDatabase({ force = false, includeInactive = false } = {}) {
  if (solarPanelModelsCache && !force && !includeInactive) return solarPanelModelsCache;

  const client = await requireActiveSupabase('Load solar panel database');
  if (!client) {
    const rows = localLoadSolarPanelRows()
      .map(panelRowFromStorage)
      .filter(Boolean)
      .filter((panel) => includeInactive || panel.active !== false);
    if (!includeInactive) solarPanelModelsCache = setSolarPanelModelsFromDatabase(rows);
    return rows;
  }

  let query = client
    .from('solar_panels')
    .select('id, manufacturer, model, watts, width_in, height_in, thickness_in, active')
    .order('manufacturer', { ascending: true })
    .order('watts', { ascending: true });

  if (!includeInactive) query = query.eq('active', true);

  let response;
  try {
    response = await query;
  } catch (error) {
    if (isInvalidRefreshTokenError(error)) throw friendlySupabaseError(error, 'Load solar panel database');
    console.warn('Load solar panel database failed. Using local fallback panel list.', error.message || error);
    const rows = localLoadSolarPanelRows()
      .map(panelRowFromStorage)
      .filter(Boolean)
      .filter((panel) => includeInactive || panel.active !== false);
    if (!includeInactive) solarPanelModelsCache = setSolarPanelModelsFromDatabase(rows);
    return rows;
  }

  const { data, error } = response;
  if (error) {
    if (isInvalidRefreshTokenError(error)) throw friendlySupabaseError(error, 'Load solar panel database');
    console.warn('Load solar panel database failed. Using local fallback panel list.', error.message || error);
    const rows = localLoadSolarPanelRows()
      .map(panelRowFromStorage)
      .filter(Boolean)
      .filter((panel) => includeInactive || panel.active !== false);
    if (!includeInactive) solarPanelModelsCache = setSolarPanelModelsFromDatabase(rows);
    return rows;
  }

  const rows = (data || []).map(solarPanelFromRow).filter(Boolean);
  if (!includeInactive) solarPanelModelsCache = setSolarPanelModelsFromDatabase(rows);
  return rows;
}

export async function loadSolarPanels(options = {}) {
  return loadSolarPanelDatabase(options);
}

export async function refreshSolarPanels() {
  solarPanelModelsCache = null;
  return loadSolarPanels({ force: true });
}

export async function createSolarPanel(panel) {
  const payload = solarPanelPayload(panel);
  const client = await requireActiveSupabase('Create solar panel');
  solarPanelModelsCache = null;

  if (!client) {
    const rows = localLoadSolarPanelRows();
    const created = { ...payload, id: makeId('panel_model'), created_at: nowIso(), updated_at: nowIso() };
    rows.unshift(created);
    localSaveSolarPanelRows(rows);
    return solarPanelFromRow(created);
  }

  const { data, error } = await client
    .from('solar_panels')
    .insert(payload)
    .select('id, manufacturer, model, watts, width_in, height_in, thickness_in, active')
    .single();
  if (error) throw friendlySupabaseError(error, 'Create solar panel');
  return solarPanelFromRow(data);
}

export async function updateSolarPanel(id, panel) {
  if (!id) throw new Error('Solar panel ID is required.');
  const payload = solarPanelPayload(panel);
  const client = await requireActiveSupabase('Update solar panel');
  solarPanelModelsCache = null;

  if (!client) {
    const rows = localLoadSolarPanelRows();
    const nextRows = rows.map((row) => (row.id === id ? { ...row, ...payload, id, updated_at: nowIso() } : row));
    localSaveSolarPanelRows(nextRows);
    return solarPanelFromRow(nextRows.find((row) => row.id === id));
  }

  const { data, error } = await client
    .from('solar_panels')
    .update(payload)
    .eq('id', id)
    .select('id, manufacturer, model, watts, width_in, height_in, thickness_in, active')
    .single();
  if (error) throw friendlySupabaseError(error, 'Update solar panel');
  return solarPanelFromRow(data);
}

export async function deleteSolarPanel(id) {
  if (!id) throw new Error('Solar panel ID is required.');
  const client = await requireActiveSupabase('Delete solar panel');
  solarPanelModelsCache = null;

  if (!client) {
    const rows = localLoadSolarPanelRows().filter((row) => row.id !== id);
    localSaveSolarPanelRows(rows);
    return;
  }

  const { error } = await client.from('solar_panels').update({ active: false }).eq('id', id);
  if (error) throw friendlySupabaseError(error, 'Delete solar panel');
}

export function usingSupabase() {
  return isSupabaseConfigured;
}

function normalizeAppSession(session = {}) {
  const issuedAt = Number(session.issuedAt) || Date.now();
  const expiresAt = Number(session.expiresAt) || issuedAt + APP_SESSION_DURATION_MS;
  return {
    ...session,
    issuedAt,
    expiresAt,
    sessionDurationHours: APP_SESSION_DURATION_HOURS,
  };
}

function isAppSessionExpired(session = {}) {
  return Boolean(session.expiresAt && Number(session.expiresAt) <= Date.now());
}

export function getSession() {
  const raw = localStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    const parsedRaw = JSON.parse(raw);
    const parsed = normalizeAppSession(parsedRaw);
    if (isAppSessionExpired(parsed)) {
      clearSession();
      return null;
    }
    if (!parsedRaw.issuedAt || !parsedRaw.expiresAt || parsedRaw.sessionDurationHours !== APP_SESSION_DURATION_HOURS) {
      setSession(parsed);
    }
    return parsed;
  } catch {
    clearSession();
    return null;
  }
}

export function setSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(normalizeAppSession(session)));
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
}

function sessionFromUser(user) {
  const email = user?.email || '';
  const metaName = user?.user_metadata?.name || user?.user_metadata?.full_name || '';
  const fallbackName = email.split('@')[0]?.replace(/[._-]/g, ' ') || 'N3bula User';
  const name = metaName || fallbackName.replace(/\b\w/g, (char) => char.toUpperCase());
  return { email, name, id: user?.id || null, sessionDurationHours: APP_SESSION_DURATION_HOURS };
}

export async function validateActiveSession() {
  const appSession = getSession();
  if (!appSession) {
    clearAuthState();
    return null;
  }

  const client = await requireSupabase();
  if (!client) return appSession;

  const { data, error } = await client.auth.getSession();
  if (error || !data?.session) {
    clearAuthState();
    return null;
  }

  const nextSession = {
    ...appSession,
    ...sessionFromUser(data.session.user),
    issuedAt: appSession.issuedAt,
    expiresAt: appSession.expiresAt,
  };
  setSession(nextSession);
  return nextSession;
}

async function requireActiveSupabase(action = 'Supabase request') {
  const client = await requireSupabase();
  if (!client) return null;

  const session = await validateActiveSession();
  if (!session) {
    throw new Error(`${action} failed: your 12-hour session has expired. Please sign in again.`);
  }
  return client;
}

export async function signInWithEmailAndPassword(email, password) {
  const client = await requireSupabase();
  if (!client) {
    const fallbackName = email.split('@')[0]?.replace(/[._-]/g, ' ') || 'N3bula User';
    const session = { email, name: fallbackName.replace(/\b\w/g, (char) => char.toUpperCase()) };
    setSession(session);
    return { session };
  }

  clearAuthState();
  const { data, error } = await client.auth.signInWithPassword({ email, password });
  if (error) throw friendlySupabaseError(error, 'Sign in');
  const session = sessionFromUser(data.user);
  setSession(session);
  return { session };
}

export async function signUpWithEmailAndPassword(email, password) {
  const client = await requireSupabase();
  if (!client) return signInWithEmailAndPassword(email, password);

  const { data, error } = await client.auth.signUp({
    email,
    password,
    options: {
      data: {
        name: email.split('@')[0]?.replace(/[._-]/g, ' ') || 'N3bula User',
      },
    },
  });
  if (error) throw friendlySupabaseError(error, 'Create account');
  if (!data.session) return { needsEmailConfirmation: true };

  const session = sessionFromUser(data.user);
  setSession(session);
  return { session };
}

export async function signOutUser() {
  const client = await requireSupabase();
  try {
    if (client) await client.auth.signOut();
  } catch (error) {
    if (!isInvalidRefreshTokenError(error)) throw friendlySupabaseError(error, 'Sign out');
  } finally {
    clearAuthState();
  }
}


const PROJECT_LIST_SELECT = 'id, project_name, address, created_by, created_at, last_modified, design_ids';
const PROJECT_DETAILS_SELECT = `${PROJECT_LIST_SELECT}, property_line, property_line_draft, address_location`;
const DESIGN_SUMMARY_SELECT = 'id, project_id, design_name, solar_panel_model, solar_panel_quantity, created_at, created_by, last_modified, elements';

let projectListCache = null;
const projectDetailsCache = new Map();

function designSummaryFromRow(row) {
  return {
    id: row.id,
    projectId: row.project_id,
    designName: row.design_name || 'Design A',
    solarPanelModel: row.solar_panel_model || defaultSolarPanelModelName(),
    solarPanelQuantity: Number(row.solar_panel_quantity) || 0,
    createdAt: row.created_at,
    createdBy: row.created_by || 'N3bula User',
    lastModified: row.last_modified,
    elements: { ...blankElements(), ...(row.elements || {}) },
  };
}

function invalidateProjectCaches(projectId = null) {
  projectListCache = null;
  if (projectId) {
    projectDetailsCache.delete(projectId);
  } else {
    projectDetailsCache.clear();
  }
}

export async function loadProjectList({ force = false } = {}) {
  if (projectListCache && !force) return projectListCache;

  const client = await requireActiveSupabase('Load projects');
  if (!client) {
    const data = localLoadData();
    const result = { projects: data.projects || [], designs: [] };
    projectListCache = result;
    return result;
  }

  let projectsResponse;
  try {
    projectsResponse = await client
      .from('projects')
      .select(PROJECT_LIST_SELECT)
      .order('last_modified', { ascending: false });
  } catch (error) {
    throw friendlySupabaseError(error, 'Load projects');
  }

  if (projectsResponse.error) throw friendlySupabaseError(projectsResponse.error, 'Load projects');

  const result = {
    projects: (projectsResponse.data || []).map((row) => projectFromRow(row, row.design_ids || [])),
    designs: [],
  };
  projectListCache = result;
  return result;
}

export async function loadProjectDetails(projectId, { force = false } = {}) {
  if (!projectId) return { project: null, designs: [] };
  if (projectDetailsCache.has(projectId) && !force) return projectDetailsCache.get(projectId);

  const client = await requireActiveSupabase('Load project');
  if (!client) {
    const data = localLoadData();
    const project = data.projects.find((item) => item.id === projectId) || null;
    const designs = data.designs.filter((design) => design.projectId === projectId);
    const result = { project, designs };
    projectDetailsCache.set(projectId, result);
    return result;
  }

  let projectResponse;
  let designsResponse;
  try {
    [projectResponse, designsResponse] = await Promise.all([
      client.from('projects').select(PROJECT_DETAILS_SELECT).eq('id', projectId).maybeSingle(),
      client
        .from('designs')
        .select(DESIGN_SUMMARY_SELECT)
        .eq('project_id', projectId)
        .order('created_at', { ascending: true }),
    ]);
  } catch (error) {
    throw friendlySupabaseError(error, 'Load project');
  }

  if (projectResponse.error) throw friendlySupabaseError(projectResponse.error, 'Load project');
  if (designsResponse.error) throw friendlySupabaseError(designsResponse.error, 'Load designs');

  const designs = (designsResponse.data || []).map(designSummaryFromRow);
  const project = projectResponse.data
    ? projectFromRow(projectResponse.data, designs.map((design) => design.id))
    : null;
  const result = { project, designs };
  projectDetailsCache.set(projectId, result);
  return result;
}

export async function loadData() {
  await loadSolarPanels();
  const client = await requireActiveSupabase('Load projects');
  if (!client) return localLoadData();

  let projectsResponse;
  let designsResponse;
  try {
    [projectsResponse, designsResponse] = await Promise.all([
      client.from('projects').select('*').order('last_modified', { ascending: false }),
      client.from('designs').select('*').order('last_modified', { ascending: false }),
    ]);
  } catch (error) {
    throw friendlySupabaseError(error, 'Load projects');
  }

  if (projectsResponse.error) throw friendlySupabaseError(projectsResponse.error, 'Load projects');
  if (designsResponse.error) throw friendlySupabaseError(designsResponse.error, 'Load designs');

  const designs = (designsResponse.data || []).map(designFromRow);
  const designIdsByProject = designs.reduce((acc, design) => {
    acc[design.projectId] = acc[design.projectId] || [];
    acc[design.projectId].push(design.id);
    return acc;
  }, {});
  const projects = (projectsResponse.data || []).map((row) => projectFromRow(row, designIdsByProject[row.id] || []));

  return { projects, designs };
}

export async function loadDesignDetails(designId) {
  if (!designId) return { project: null, design: null };

  const client = await requireActiveSupabase('Load design');
  if (!client) {
    const data = localLoadData();
    const design = data.designs.find((item) => item.id === designId) || null;
    const project = data.projects.find((item) => item.id === design?.projectId) || null;
    return { project, design };
  }

  let designResponse;
  try {
    designResponse = await client.from('designs').select('*').eq('id', designId).maybeSingle();
  } catch (error) {
    throw friendlySupabaseError(error, 'Load design');
  }

  if (designResponse.error) throw friendlySupabaseError(designResponse.error, 'Load design');
  if (!designResponse.data) return { project: null, design: null };

  const design = designFromRow(designResponse.data);
  let projectResponse;
  try {
    projectResponse = await client.from('projects').select('*').eq('id', design.projectId).maybeSingle();
  } catch (error) {
    throw friendlySupabaseError(error, 'Load project');
  }

  if (projectResponse.error) throw friendlySupabaseError(projectResponse.error, 'Load project');
  const project = projectResponse.data ? projectFromRow(projectResponse.data, [design.id]) : null;
  return { project, design };
}

export async function saveData(data) {
  const client = await requireActiveSupabase('Save projects');
  if (!client) {
    localSaveData(data);
    invalidateProjectCaches();
    return data;
  }

  const projectRows = (data.projects || []).map(projectToRow);
  const designRows = (data.designs || []).map(designToRow);
  if (projectRows.length) {
    const { error } = await client.from('projects').upsert(projectRows);
    if (error) throw friendlySupabaseError(error, 'Save projects');
  }
  if (designRows.length) {
    const { error } = await client.from('designs').upsert(designRows);
    if (error) throw friendlySupabaseError(error, 'Save designs');
  }
  invalidateProjectCaches();
  return data;
}

export async function createProject({ projectName, address, propertyLine, addressLocation = null }) {
  await loadSolarPanels();
  const client = await requireActiveSupabase('Create project');
  const projectId = makeId('project');
  const designId = makeId('design');
  const created = nowIso();
  const map = defaultMap(propertyLine || null, addressLocation);
  const scaledPropertyLine = scalePropertyLineToMap(propertyLine, map);
  const createdBy = getSession()?.name || 'N3bula User';
  const project = {
    id: projectId,
    projectName,
    address,
    createdBy,
    createdAt: created,
    lastModified: created,
    designIds: [designId],
    propertyLine: scaledPropertyLine,
    propertyLineDraft: normalizePropertyDraft(propertyLine),
    addressLocation,
  };
  const design = {
    id: designId,
    projectId,
    designName: 'Design A',
    solarPanelModel: defaultSolarPanelModelName(),
    solarPanelQuantity: 0,
    createdAt: created,
    createdBy,
    lastModified: created,
    layers: defaultLayers(),
    map,
    elements: {
      ...blankElements(),
      propertyLines: scaledPropertyLine.length ? [{ id: makeId('property'), points: scaledPropertyLine }] : [],
    },
  };

  if (!client) {
    const data = localLoadData();
    data.projects.unshift(project);
    data.designs.unshift(design);
    localSaveData(data);
    invalidateProjectCaches();
    return { project, design };
  }

  const { error: projectError } = await client.from('projects').insert(projectToRow(project));
  if (projectError) throw friendlySupabaseError(projectError, 'Create project');

  const { error: designError } = await client.from('designs').insert(designToRow(design));
  if (designError) throw friendlySupabaseError(designError, 'Create first design');

  invalidateProjectCaches();
  return { project, design };
}

export async function updateProject(updatedProject) {
  const client = await requireActiveSupabase('Update project');
  const project = { ...updatedProject, lastModified: nowIso() };
  if (!client) {
    const data = localLoadData();
    data.projects = data.projects.map((item) => (item.id === project.id ? project : item));
    localSaveData(data);
    invalidateProjectCaches(project.id);
    return project;
  }

  const { error } = await client.from('projects').update(projectToRow(project)).eq('id', project.id);
  if (error) throw friendlySupabaseError(error, 'Update project');
  invalidateProjectCaches(project.id);
  return project;
}

export async function deleteProject(projectId) {
  const client = await requireActiveSupabase('Delete project');
  if (!client) {
    const data = localLoadData();
    data.projects = data.projects.filter((project) => project.id !== projectId);
    data.designs = data.designs.filter((design) => design.projectId !== projectId);
    localSaveData(data);
    invalidateProjectCaches(projectId);
    return;
  }

  const { error } = await client.from('projects').delete().eq('id', projectId);
  if (error) throw friendlySupabaseError(error, 'Delete project');
  invalidateProjectCaches(projectId);
}

export async function createDesign(projectId, name = 'New Design') {
  await loadSolarPanels();
  const client = await requireActiveSupabase('Create design');
  let project = null;
  let data = null;

  if (!client) {
    data = localLoadData();
    project = data.projects.find((item) => item.id === projectId) || null;
  } else {
    let projectResponse;
    try {
      projectResponse = await client.from('projects').select(PROJECT_DETAILS_SELECT).eq('id', projectId).maybeSingle();
    } catch (error) {
      throw friendlySupabaseError(error, 'Load project for new design');
    }
    if (projectResponse.error) throw friendlySupabaseError(projectResponse.error, 'Load project for new design');
    project = projectResponse.data ? projectFromRow(projectResponse.data, projectResponse.data.design_ids || []) : null;
  }

  if (!project) return null;

  const id = makeId('design');
  const created = nowIso();
  const design = {
    id,
    projectId,
    designName: name,
    solarPanelModel: defaultSolarPanelModelName(),
    solarPanelQuantity: 0,
    createdAt: created,
    createdBy: getSession()?.name || 'N3bula User',
    lastModified: created,
    layers: defaultLayers(),
    map: defaultMap(project.propertyLine?.length ? project.propertyLine : null, project.addressLocation || null),
    elements: {
      ...blankElements(),
      propertyLines: project.propertyLine?.length ? [{ id: makeId('property'), points: clone(project.propertyLine) }] : [],
    },
  };

  if (!client) {
    project.designIds = [...new Set([...(project.designIds || []), id])];
    project.lastModified = created;
    data.designs.unshift(design);
    localSaveData(data);
    invalidateProjectCaches(projectId);
    return design;
  }

  const { error: designError } = await client.from('designs').insert(designToRow(design));
  if (designError) throw friendlySupabaseError(designError, 'Create design');

  const nextDesignIds = [...new Set([...(project.designIds || []), id])];
  const { error: projectError } = await client
    .from('projects')
    .update({ design_ids: nextDesignIds, last_modified: created })
    .eq('id', projectId);
  if (projectError) throw friendlySupabaseError(projectError, 'Update project design list');

  invalidateProjectCaches(projectId);
  return design;
}

export async function copyDesign(designId) {
  const client = await requireActiveSupabase('Copy design');
  let original = null;
  let project = null;
  let data = null;

  if (!client) {
    data = localLoadData();
    original = data.designs.find((design) => design.id === designId) || null;
    project = data.projects.find((item) => item.id === original?.projectId) || null;
  } else {
    let designResponse;
    try {
      designResponse = await client.from('designs').select('*').eq('id', designId).maybeSingle();
    } catch (error) {
      throw friendlySupabaseError(error, 'Load design to copy');
    }
    if (designResponse.error) throw friendlySupabaseError(designResponse.error, 'Load design to copy');
    original = designResponse.data ? designFromRow(designResponse.data) : null;

    if (original) {
      let projectResponse;
      try {
        projectResponse = await client.from('projects').select(PROJECT_DETAILS_SELECT).eq('id', original.projectId).maybeSingle();
      } catch (error) {
        throw friendlySupabaseError(error, 'Load project for copied design');
      }
      if (projectResponse.error) throw friendlySupabaseError(projectResponse.error, 'Load project for copied design');
      project = projectResponse.data ? projectFromRow(projectResponse.data, projectResponse.data.design_ids || []) : null;
    }
  }

  if (!original) return null;

  const copy = clone(original);
  copy.id = makeId('design');
  copy.designName = `${original.designName} Copy`;
  copy.createdAt = nowIso();
  copy.lastModified = copy.createdAt;
  copy.elements = { ...blankElements(), ...(copy.elements || {}) };
  copy.elements.roofs = (copy.elements.roofs || []).map((item) => ({ ...item, id: makeId('roof') }));
  copy.elements.propertyLines = (copy.elements.propertyLines || []).map((item) => ({ ...item, id: makeId('property') }));
  copy.elements.obstructions = (copy.elements.obstructions || []).map((item) => ({ ...item, id: makeId('obstruction') }));
  copy.elements.fireSetbacks = (copy.elements.fireSetbacks || []).map((item) => ({ ...item, id: makeId('fire') }));
  copy.elements.solarPanels = (copy.elements.solarPanels || []).map((item) => ({ ...item, id: makeId('panel') }));

  if (!client) {
    data.designs.unshift(copy);
    data.projects = data.projects.map((item) => (
      item.id === copy.projectId
        ? { ...item, designIds: [...new Set([...(item.designIds || []), copy.id])], lastModified: copy.lastModified }
        : item
    ));
    localSaveData(data);
    invalidateProjectCaches(copy.projectId);
    return copy;
  }

  const { error: designError } = await client.from('designs').insert(designToRow(copy));
  if (designError) throw friendlySupabaseError(designError, 'Copy design');

  const nextDesignIds = [...new Set([...(project?.designIds || []), copy.id])];
  const { error: projectError } = await client
    .from('projects')
    .update({ design_ids: nextDesignIds, last_modified: copy.lastModified })
    .eq('id', copy.projectId);
  if (projectError) throw friendlySupabaseError(projectError, 'Update project design list');

  invalidateProjectCaches(copy.projectId);
  return copy;
}

export async function updateDesign(updatedDesign) {
  const client = await requireActiveSupabase('Update design');
  const lastModified = nowIso();
  const design = { ...updatedDesign, lastModified };

  if (!client) {
    const data = localLoadData();
    data.designs = data.designs.map((item) => (item.id === design.id ? design : item));
    data.projects = data.projects.map((project) => (project.id === design.projectId ? { ...project, lastModified } : project));
    localSaveData(data);
    invalidateProjectCaches(design.projectId);
    return design;
  }

  const { error: designError } = await client.from('designs').update(designToRow(design)).eq('id', design.id);
  if (designError) throw friendlySupabaseError(designError, 'Save design');

  const { error: projectError } = await client.from('projects').update({ last_modified: lastModified }).eq('id', design.projectId);
  if (projectError) throw friendlySupabaseError(projectError, 'Update project modified date');

  invalidateProjectCaches(design.projectId);
  return design;
}

export async function deleteDesign(designId) {
  const client = await requireActiveSupabase('Delete design');
  let design = null;
  let project = null;
  let data = null;

  if (!client) {
    data = localLoadData();
    design = data.designs.find((item) => item.id === designId) || null;
    project = data.projects.find((item) => item.id === design?.projectId) || null;

    data.designs = data.designs.filter((item) => item.id !== designId);
    if (design) {
      data.projects = data.projects.map((item) => (
        item.id === design.projectId
          ? { ...item, designIds: (item.designIds || []).filter((id) => id !== designId), lastModified: nowIso() }
          : item
      ));
    }
    localSaveData(data);
    invalidateProjectCaches(design?.projectId || null);
    return;
  }

  let designResponse;
  try {
    designResponse = await client.from('designs').select('id, project_id').eq('id', designId).maybeSingle();
  } catch (error) {
    throw friendlySupabaseError(error, 'Load design to delete');
  }
  if (designResponse.error) throw friendlySupabaseError(designResponse.error, 'Load design to delete');
  design = designResponse.data ? { id: designResponse.data.id, projectId: designResponse.data.project_id } : null;

  if (design?.projectId) {
    let projectResponse;
    try {
      projectResponse = await client.from('projects').select('id, design_ids').eq('id', design.projectId).maybeSingle();
    } catch (error) {
      throw friendlySupabaseError(error, 'Load project for design delete');
    }
    if (projectResponse.error) throw friendlySupabaseError(projectResponse.error, 'Load project for design delete');
    project = projectResponse.data ? projectFromRow(projectResponse.data, projectResponse.data.design_ids || []) : null;
  }

  const { error: deleteError } = await client.from('designs').delete().eq('id', designId);
  if (deleteError) throw friendlySupabaseError(deleteError, 'Delete design');

  if (design?.projectId) {
    const nextDesignIds = (project?.designIds || []).filter((id) => id !== designId);
    const { error: projectError } = await client
      .from('projects')
      .update({ design_ids: nextDesignIds, last_modified: nowIso() })
      .eq('id', design.projectId);
    if (projectError) throw friendlySupabaseError(projectError, 'Update project design list');
  }

  invalidateProjectCaches(design?.projectId || null);
}